import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AspectRatioOutlined from "@mui/icons-material/AspectRatioOutlined";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import BoltOutlined from "@mui/icons-material/BoltOutlined";
import ChatBubbleOutlineOutlined from "@mui/icons-material/ChatBubbleOutlineOutlined";
import CropLandscapeOutlined from "@mui/icons-material/CropLandscapeOutlined";
import CropPortraitOutlined from "@mui/icons-material/CropPortraitOutlined";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import PaletteOutlined from "@mui/icons-material/PaletteOutlined";
import MicOutlined from "@mui/icons-material/MicOutlined";
import MovieFilterOutlined from "@mui/icons-material/MovieFilterOutlined";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import TimerOutlined from "@mui/icons-material/TimerOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import VideoCameraFrontOutlined from "@mui/icons-material/VideoCameraFrontOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { GlobalContext } from "../../context/GlobalContext";
import PageHeader from "../../common/PageHeader";
import CommonDialog from "../../common/CommonDialog";
import VideoAgentJobCard from "./VideoAgentJobCard";
import VideoAgentJobDetailModal from "./VideoAgentJobDetailModal";
import {
  ChooseAvatarDialog,
  ChooseStyleDialog,
  ChooseVoiceDialog,
  PhotoClassicDialog,
} from "./VideoAgentModals";

const HEYGEN_APP = "https://app.heygen.com/";

/** Match Actors “Looks” shelf: hide studio avatars and known clutter names. */
const EXCLUDED_VIDEO_AGENT_LOOK_NAMES = new Set(["Brandon_Business_Sitting_Side_public"]);

function filterVideoAgentLook(lk) {
  if (!lk || typeof lk !== "object") return false;
  if (lk.avatar_type === "studio_avatar") return false;
  if (EXCLUDED_VIDEO_AGENT_LOOK_NAMES.has(String(lk.name || ""))) return false;
  return true;
}

function firstNonEmptyString(...vals) {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

function pickLookPreviewImage(lk) {
  if (!lk || typeof lk !== "object") return null;
  return firstNonEmptyString(
    lk.preview_image_url,
    lk.preview_image,
    lk.previewImageUrl,
    lk.image_url,
    lk.photo_url,
    lk.thumbnail_url,
    lk.cover_image_url,
    lk?.preview?.image_url,
    lk?.preview?.url,
  );
}

function mapV3LookToPickerRow(lk, ownership) {
  return {
    avatar_id: lk.id,
    avatar_name: lk.name,
    preview_image_url: pickLookPreviewImage(lk),
    tags: lk.tags,
    avatar_type: lk.avatar_type,
    _vaOwnership: ownership === "private" ? "mine" : "public",
  };
}

async function fetchV3LooksForVideoAgent(hitAxios, ownership, maxPages = 8) {
  const out = [];
  let token = null;
  for (let p = 0; p < maxPages; p++) {
    const q = new URLSearchParams();
    q.set("ownership", ownership);
    q.set("limit", "50");
    if (token) q.set("token", token);
    const res = await hitAxios({
      path: `/api/heygen/v3/avatar-looks?${q}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    if (!res?.data?.success) break;
    const looks = res.data.data?.looks || [];
    for (const lk of looks) {
      if (!filterVideoAgentLook(lk)) continue;
      out.push(mapV3LookToPickerRow(lk, ownership));
    }
    if (!res.data.data?.has_more) break;
    token = res.data.data?.next_token || null;
    if (!token) break;
  }
  return out;
}

export default function VideoAgent({ lang }) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const { hitAxios, showSnack } = React.useContext(GlobalContext);

  const [primaryMode, setPrimaryMode] = React.useState("agent");
  const [voices, setVoices] = React.useState([]);
  const [avatars, setAvatars] = React.useState([]);
  const [talkingPhotos, setTalkingPhotos] = React.useState([]);
  const [apiStyles, setApiStyles] = React.useState([]);
  const [loadError, setLoadError] = React.useState("");

  const [picker, setPicker] = React.useState(null);
  const [addMenuEl, setAddMenuEl] = React.useState(null);
  const [photoDialogOpen, setPhotoDialogOpen] = React.useState(false);

  const [selectedLookId, setSelectedLookId] = React.useState("");
  const [selectedTalkingPhotoId, setSelectedTalkingPhotoId] = React.useState("");
  const [selectedVoiceId, setSelectedVoiceId] = React.useState("");
  const [selectedStyle, setSelectedStyle] = React.useState(null);

  const [script, setScript] = React.useState("");
  const [aspectMode, setAspectMode] = React.useState("auto");
  const [agentOrientation, setAgentOrientation] = React.useState("auto");
  const [sessionMode, setSessionMode] = React.useState("generate");
  const [durationMode, setDurationMode] = React.useState("auto");
  const [privateVis, setPrivateVis] = React.useState(false);
  const [seedance, setSeedance] = React.useState(false);
  const [brandKits, setBrandKits] = React.useState([]);
  const [brandKitEnabled, setBrandKitEnabled] = React.useState(false);
  const [selectedBrandKitId, setSelectedBrandKitId] = React.useState("");

  const [voicePreviewUrl, setVoicePreviewUrl] = React.useState(null);
  const [voicePreviewPlaying, setVoicePreviewPlaying] = React.useState(false);
  const voiceAudioRef = React.useRef(null);

  const [generating, setGenerating] = React.useState(false);
  const [jobVideoId, setJobVideoId] = React.useState("");
  const [jobSessionId, setJobSessionId] = React.useState("");
  const [jobStatus, setJobStatus] = React.useState("");
  const [jobVideoUrl, setJobVideoUrl] = React.useState("");
  const pollRef = React.useRef(null);

  const [speechListening, setSpeechListening] = React.useState(false);
  const recognitionRef = React.useRef(null);

  const [jobs, setJobs] = React.useState([]);
  const [jobsLoading, setJobsLoading] = React.useState(true);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [detailModalOpen, setDetailModalOpen] = React.useState(false);
  const [selectedListJob, setSelectedListJob] = React.useState(null);

  React.useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      try {
        recognitionRef.current?.stop?.();
      } catch {
        /* ignore */
      }
      setSpeechListening(false);
    };
  }, []);

  const loadCatalog = React.useCallback(async () => {
    setLoadError("");
    const [vRes, tpRes, sRes, bkRes, privLooks, pubLooks] = await Promise.all([
      hitAxios({
        path: "/api/heygen/voices",
        post: false,
        showLoading: false,
        showSnackbar: false,
      }),
      hitAxios({
        path: "/api/heygen/avatars",
        post: false,
        showLoading: false,
        showSnackbar: false,
      }),
      hitAxios({
        path: "/api/heygen/video/agent/styles",
        post: false,
        showLoading: false,
        showSnackbar: false,
      }),
      hitAxios({
        path: "/api/heygen/video/agent/brand-kits?limit=50",
        post: false,
        showLoading: false,
        showSnackbar: false,
      }),
      fetchV3LooksForVideoAgent(hitAxios, "private"),
      fetchV3LooksForVideoAgent(hitAxios, "public"),
    ]);

    let errMsg = "";
    if (vRes?.data?.success && Array.isArray(vRes.data.data?.voices)) {
      setVoices(vRes.data.data.voices);
    } else {
      setVoices([]);
      errMsg = vRes?.data?.msg || "";
    }

    setAvatars([...privLooks, ...pubLooks]);

    if (tpRes?.data?.success && tpRes.data.data) {
      setTalkingPhotos(
        Array.isArray(tpRes.data.data.talking_photos) ? tpRes.data.data.talking_photos : [],
      );
    } else {
      setTalkingPhotos([]);
      errMsg = errMsg || tpRes?.data?.msg || "";
    }

    if (sRes?.data?.success && Array.isArray(sRes.data.data?.styles)) {
      setApiStyles(sRes.data.data.styles);
    } else {
      setApiStyles([]);
    }

    if (bkRes?.data?.success && Array.isArray(bkRes.data.data?.brand_kits)) {
      setBrandKits(bkRes.data.data.brand_kits);
    } else {
      setBrandKits([]);
    }

    setLoadError(errMsg);
  }, [hitAxios]);

  const fetchJobs = React.useCallback(async () => {
    setJobsLoading(true);
    const res = await hitAxios({
      path: "/api/heygen/video-agent/jobs",
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    setJobsLoading(false);
    if (res?.data?.success && Array.isArray(res.data.data?.jobs)) {
      setJobs(res.data.data.jobs);
    } else {
      setJobs([]);
    }
  }, [hitAxios]);

  React.useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  React.useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  React.useEffect(() => {
    const el = voiceAudioRef.current;
    if (!el) return undefined;
    const onEnd = () => setVoicePreviewPlaying(false);
    el.addEventListener("ended", onEnd);
    return () => el.removeEventListener("ended", onEnd);
  }, []);

  const selectedVoice = voices.find((v) => v.voice_id === selectedVoiceId);
  const selectedAvatarRow = React.useMemo(
    () => avatars.find((a) => a.avatar_id === selectedLookId) || null,
    [avatars, selectedLookId],
  );

  const avatarDisplayName = React.useMemo(() => {
    if (!selectedLookId) return lang?.videoAgentStyleAutoCard || "Auto";
    const raw = String(selectedAvatarRow?.avatar_name || "").trim();
    if (!raw) return lang?.videoAgentPickerAvatar || "Avatar";
    return raw.split("(")[0].split("–")[0].split(" - ")[0].trim() || raw;
  }, [selectedLookId, selectedAvatarRow, lang]);

  const voiceDisplayName = selectedVoice?.name || lang?.videoAgentStyleAutoCard || "Auto";

  const styleDisplayName = selectedStyle?.name || lang?.videoAgentStyleAutoCard || "Auto";

  const toggleVoicePreview = (v) => {
    const url = v?.preview_audio;
    if (!url) return;
    const el = voiceAudioRef.current;
    if (!el) return;
    if (voicePreviewPlaying && voicePreviewUrl === url) {
      el.pause();
      setVoicePreviewPlaying(false);
      return;
    }
    el.src = url;
    setVoicePreviewUrl(url);
    el.play().catch(() => {
      showSnack(lang?.videoAgentVoicePreviewError || "Could not play voice preview.", false);
      setVoicePreviewPlaying(false);
    });
    setVoicePreviewPlaying(true);
  };

  const startSpeechToText = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      showSnack(
        lang?.videoAgentSpeechNotSupported ||
          "Speech-to-text is not available in this browser. Type or paste your text.",
        false,
      );
      return;
    }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
      setSpeechListening(false);
      return;
    }
    const rec = new SR();
    rec.lang = "en-US";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (ev) => {
      let chunk = "";
      for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
        chunk += ev.results[i][0].transcript;
      }
      if (chunk) {
        setScript((prev) => (prev ? `${prev} ${chunk}`.trim() : chunk.trim()));
      }
    };
    rec.onerror = () => {
      setSpeechListening(false);
      recognitionRef.current = null;
      showSnack(lang?.videoAgentSpeechError || "Speech capture stopped.", false);
    };
    rec.onend = () => {
      setSpeechListening(false);
      recognitionRef.current = null;
    };
    rec.start();
    recognitionRef.current = rec;
    setSpeechListening(true);
    showSnack(lang?.videoAgentSpeechOn || "Listening… click the mic again to stop.", true);
  };

  const stopSpeechToText = () => {
    try {
      recognitionRef.current?.stop?.();
    } catch {
      /* ignore */
    }
    recognitionRef.current = null;
    setSpeechListening(false);
  };

  const toggleMic = () => {
    if (recognitionRef.current) stopSpeechToText();
    else startSpeechToText();
  };

  const canGenerate =
    primaryMode === "agent"
      ? script.trim().length >= 1 &&
          script.trim().length <= 10000 &&
          (!brandKitEnabled ||
            brandKits.length === 0 ||
            (brandKits.length > 0 && !!String(selectedBrandKitId || "").trim()))
      : script.trim().length >= 8 && !!selectedVoiceId && !!selectedTalkingPhotoId;

  const pollStatus = React.useCallback(
    async (videoId) => {
      const res = await hitAxios({
        path: `/api/heygen/video/status?video_id=${encodeURIComponent(videoId)}`,
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (!res?.data?.success) {
        setJobStatus("error");
        return true;
      }
      const d = res.data.data || {};
      const statusRaw = String(d.status || d.video_status || d.state || "").toLowerCase();
      setJobStatus(statusRaw || "processing");
      const url = d.video_url || d.url || d.download_url || "";
      if (url) setJobVideoUrl(String(url));
      if (statusRaw.includes("complete") || statusRaw === "succeeded" || statusRaw === "done") {
        showSnack(lang?.videoAgentVideoReady || "Your video is ready.", true);
        return true;
      }
      if (statusRaw.includes("fail") || statusRaw === "error") {
        showSnack(
          String(d.error || d.message || lang?.videoAgentVideoFailed || "Video generation failed."),
          false,
        );
        return true;
      }
      return false;
    },
    [hitAxios, showSnack, lang],
  );

  const handleDeleteJob = async (job) => {
    if (!job?.id) return;
    if (
      !window.confirm(
        lang?.videoAgentDeleteConfirm || "Delete this video job? Stored files will be removed.",
      )
    ) {
      return;
    }
    const res = await hitAxios({
      path: "/api/heygen/video-agent/jobs/delete",
      post: true,
      obj: { id: job.id },
      showLoading: false,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      fetchJobs();
      if (selectedListJob?.id === job.id) {
        setDetailModalOpen(false);
        setSelectedListJob(null);
      }
    } else {
      showSnack(res?.data?.msg || lang?.videoAgentGenerateError || "Could not delete.", false);
    }
  };

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setJobVideoId("");
    setJobSessionId("");
    setJobStatus("submitting");
    setJobVideoUrl("");
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    try {
      if (primaryMode === "agent") {
        const body = {
          prompt: script.trim(),
          mode: sessionMode === "chat" ? "chat" : "generate",
        };
        if (selectedVoiceId) body.voice_id = selectedVoiceId;
        if (agentOrientation === "landscape" || agentOrientation === "portrait") {
          body.orientation = agentOrientation;
        }
        if (brandKitEnabled && selectedBrandKitId) {
          body.brand_kit_id = String(selectedBrandKitId).trim();
        }
        if (selectedLookId) body.avatar_id = selectedLookId;
        const sid = selectedStyle?.id;
        if (sid && !String(sid).startsWith("__fb_")) body.style_id = String(sid);

        const res = await hitAxios({
          path: "/api/heygen/video-agent/jobs",
          post: true,
          obj: body,
          showLoading: false,
          showSnackbar: false,
        });
        if (!res?.data?.success || !res.data.data?.job?.id) {
          showSnack(res?.data?.msg || lang?.videoAgentGenerateError || "Could not start video.", false);
          setJobStatus("error");
          setGenerating(false);
          return;
        }
        const jobId = res.data.data.job.id;
        setJobVideoId(String(res.data.data.job.heygen_video_id || ""));
        setJobSessionId(String(res.data.data.job.heygen_session_id || ""));
        setJobStatus(String(res.data.data.job.status || "processing"));
        setJobVideoUrl("");

        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
        let finalized = false;
        for (let attempt = 0; attempt < 180 && !finalized; attempt++) {
          const pr = await hitAxios({
            path: `/api/heygen/video-agent/jobs/${jobId}/poll`,
            post: true,
            obj: {},
            showLoading: false,
            showSnackbar: false,
          });
          if (!pr?.data?.success) {
            showSnack(pr?.data?.msg || lang?.videoAgentGenerateError || "Status check failed.", false);
            break;
          }
          const job = pr.data.data?.job;
          const fin = Boolean(pr.data.data?.finalized);
          if (job) {
            setJobStatus(String(job.status || ""));
            setJobVideoId(String(job.heygen_video_id || ""));
            setJobSessionId(String(job.heygen_session_id || ""));
            if (job.generated_video) {
              setJobVideoUrl(String(job.generated_video));
            }
          }
          if (fin) {
            finalized = true;
            if (job?.status === "completed") {
              showSnack(lang?.videoAgentVideoReady || "Your video is saved to your library.", true);
            }
            if (job?.status === "failed") {
              showSnack(
                String(job.error_message || lang?.videoAgentVideoFailed || "Video generation failed."),
                false,
              );
            }
            break;
          }
          const hasVid = String(job?.heygen_video_id || "").trim();
          if (!hasVid && attempt >= 8) {
            showSnack(
              lang?.videoAgentSessionNoVideoId ||
                "Still waiting for a HeyGen video id. Open this job later and tap Check status in the details view.",
              true,
            );
            break;
          }
          await sleep(3500);
        }

        await fetchJobs();
        setGenerating(false);
        setCreateDialogOpen(false);
        return;
      }

      const body = {
        character_type: "talking_photo",
        voice_id: selectedVoiceId,
        script: script.trim(),
        aspect_ratio: aspectMode === "9:16" ? "9:16" : "16:9",
        use_avatar_iv: false,
        title: lang?.videoAgentClassicTitle || "Photo avatar — ThePlatform",
        talking_photo_id: selectedTalkingPhotoId,
      };

      const res = await hitAxios({
        path: "/api/heygen/video/broll",
        post: true,
        obj: body,
        showLoading: false,
        showSnackbar: false,
      });
      if (!res?.data?.success || !res.data.data?.video_id) {
        showSnack(res?.data?.msg || lang?.videoAgentGenerateError || "Could not start video.", false);
        setJobStatus("error");
        setGenerating(false);
        return;
      }
      const vid = res.data.data.video_id;
      setJobVideoId(vid);
      setJobStatus("queued");
      showSnack(lang?.videoAgentQueued || "Video queued. Rendering…", true);

      const tick = async () => {
        const done = await pollStatus(vid);
        if (done) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
        }
      };
      pollRef.current = setInterval(tick, 3500);
      await tick();
    } catch {
      showSnack(lang?.videoAgentGenerateError || "Could not start video.", false);
      setJobStatus("error");
      setGenerating(false);
    }
  };

  const pickerCardSx = {
    flex: 1,
    minWidth: { xs: "100%", sm: 0 },
    borderRadius: 2.5,
    p: 1.75,
    cursor: "pointer",
    textAlign: "left",
    border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
    bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.55 : 0.94),
    boxShadow:
      theme.palette.mode === "dark"
        ? `0 4px 22px ${alpha("#000", 0.35)}`
        : `0 8px 26px ${alpha(theme.palette.primary.main, 0.07)}`,
    transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow:
        theme.palette.mode === "dark"
          ? `0 12px 40px ${alpha("#000", 0.5)}`
          : `0 14px 40px ${alpha(theme.palette.primary.main, 0.14)}`,
      borderColor: alpha(theme.palette.primary.main, 0.45),
    },
  };

  return (
    <div>
      <audio ref={voiceAudioRef} hidden />

      <ChooseAvatarDialog
        open={picker === "avatar"}
        onClose={() => setPicker(null)}
        lang={lang}
        avatars={avatars}
        selectedLookId={selectedLookId}
        onPick={(row) => {
          setSelectedLookId(row.avatar_id);
        }}
      />
      <ChooseVoiceDialog
        open={picker === "voice"}
        onClose={() => setPicker(null)}
        lang={lang}
        voices={voices}
        selectedVoiceId={selectedVoiceId}
        onPick={(id) => setSelectedVoiceId(id)}
        showSnack={showSnack}
        voicePreviewPlaying={voicePreviewPlaying}
        voicePreviewUrl={voicePreviewUrl}
        onTogglePreview={toggleVoicePreview}
      />
      <ChooseStyleDialog
        open={picker === "style"}
        onClose={() => setPicker(null)}
        lang={lang}
        apiStyles={apiStyles}
        selectedStyleId={selectedStyle?.id || null}
        onPick={(st) => setSelectedStyle(st)}
      />
      <PhotoClassicDialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        lang={lang}
        talkingPhotos={talkingPhotos}
        selectedTalkingPhotoId={selectedTalkingPhotoId}
        onPickPhoto={setSelectedTalkingPhotoId}
        script={script}
        onScript={setScript}
      />

      <VideoAgentJobDetailModal
        item={selectedListJob}
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedListJob(null);
        }}
        lang={lang}
        hitAxios={hitAxios}
        showSnack={showSnack}
        onRefreshList={fetchJobs}
      />

      <Box sx={{ maxWidth: 1040, mx: "auto" }}>
        <PageHeader
          icon={SmartToyOutlined}
          title={lang?.videoAgentTitle || "Video Agent"}
          subtitle={
            lang?.videoAgentHubSubtitle ||
            "Your Video Agent clips are saved here. Open one to play or download the stored file."
          }
          primaryAction={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
            >
              {lang?.videoAgentNewVideo || "New video"}
            </Button>
          }
        />

        {loadError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {jobsLoading ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : jobs.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, px: 2, mb: 2 }}>
            <SmartToyOutlined sx={{ fontSize: 72, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {lang?.videoAgentEmptyTitle || "No videos yet"}
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              {lang?.videoAgentEmptySub ||
                "Create your first Video Agent clip. It will appear here when it finishes and we'll store the file."}
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
              {lang?.videoAgentNewVideo || "New video"}
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {jobs.map((j) => (
              <Grid item xs={12} sm={6} md={4} key={j.id}>
                <VideoAgentJobCard
                  item={j}
                  lang={lang}
                  onClick={() => {
                    setSelectedListJob(j);
                    setDetailModalOpen(true);
                  }}
                  onDelete={handleDeleteJob}
                />
              </Grid>
            ))}
          </Grid>
        )}

        <CommonDialog
          open={createDialogOpen}
          onClose={() => {
            if (!generating) setCreateDialogOpen(false);
          }}
          title={lang?.videoAgentCreateDialogTitle || "New Video Agent"}
          subtitle={
            lang?.videoAgentCreateDialogSub ||
            "Describe your video, choose mode and format, then generate. We save the finished file to your workspace."
          }
          icon={SmartToyOutlined}
          maxWidth="lg"
          contentSx={{ px: { xs: 1.5, sm: 2 }, pt: 0 }}
        >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.25, sm: 3, md: 3.5 },
            borderRadius: 3,
            border: "1px solid transparent",
            backgroundImage: (t) =>
              `linear-gradient(${t.palette.background.paper}, ${t.palette.background.paper}), linear-gradient(135deg, ${alpha(
                t.palette.primary.main,
                0.35,
              )}, ${alpha("#ec4899", 0.25)}, ${alpha("#f97316", 0.3)})`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 24px 80px ${alpha("#000", 0.45)}`
                : `0 20px 56px ${alpha(theme.palette.primary.main, 0.08)}`,
          }}
        >
        {primaryMode === "photo" && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            {lang?.videoAgentPhotoModeBanner ||
              "Photo avatar mode: configure script and character via the toolbar, then Generate."}
          </Alert>
        )}

        {primaryMode === "agent" && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase" }}
            >
              {lang?.videoAgentSessionModeLabel || "Session mode"}
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={sessionMode}
              onChange={(_, v) => v != null && setSessionMode(v)}
              fullWidth
              sx={{
                p: 0.5,
                borderRadius: 2.5,
                bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.08 : 0.05),
                gap: 0.75,
                "& .MuiToggleButton-root": {
                  flex: 1,
                  py: 1.35,
                  textTransform: "none",
                  fontWeight: 800,
                  border: "none",
                  borderRadius: "12px !important",
                  "&.Mui-selected": {
                    bgcolor: "background.paper",
                    boxShadow: (t) =>
                      t.palette.mode === "dark"
                        ? `0 2px 12px ${alpha("#000", 0.45)}`
                        : `0 4px 18px ${alpha(t.palette.primary.main, 0.18)}`,
                  },
                },
              }}
            >
              <ToggleButton value="generate" disableRipple>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <BoltOutlined fontSize="small" />
                  <span>{lang?.videoAgentModeGenerate || "Generate"}</span>
                </Stack>
              </ToggleButton>
              <ToggleButton value="chat" disableRipple>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <ChatBubbleOutlineOutlined fontSize="small" />
                  <span>{lang?.videoAgentModeChat || "Chat"}</span>
                </Stack>
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: "block", lineHeight: 1.5 }}>
              {sessionMode === "chat"
                ? lang?.videoAgentModeChatHint ||
                  "Multi-turn: the agent may pause for your choices before it renders."
                : lang?.videoAgentModeGenerateHint ||
                  "One-shot: the agent runs storyboard and rendering in a single pass."}
            </Typography>
          </Box>
        )}

        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5, letterSpacing: 0.2 }}>
          {lang?.videoAgentCastRowTitle || "Cast & look"}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ mb: 2.5 }}
          alignItems="stretch"
        >
          <Box
            component="button"
            type="button"
            onClick={() => primaryMode === "agent" && setPicker("avatar")}
            disabled={primaryMode !== "agent"}
            sx={{
              ...pickerCardSx,
              opacity: primaryMode === "agent" ? 1 : 0.55,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              font: "inherit",
              color: "inherit",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                overflow: "hidden",
                flexShrink: 0,
                bgcolor: "grey.800",
                border: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
              }}
            >
              {selectedAvatarRow?.preview_image_url ? (
                <Box
                  component="img"
                  src={selectedAvatarRow.preview_image_url}
                  alt=""
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    typography: "caption",
                    fontWeight: 800,
                  }}
                >
                  AI
                </Box>
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} noWrap>
                {avatarDisplayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lang?.videoAgentPickerAvatar || "Avatar"}
              </Typography>
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => setPicker("voice")}
            sx={{
              ...pickerCardSx,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              font: "inherit",
              color: "inherit",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                flexShrink: 0,
                bgcolor: alpha(theme.palette.primary.main, 0.25),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PlayArrowOutlined sx={{ color: "primary.main" }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} noWrap>
                {voiceDisplayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lang?.videoAgentPickerVoice || "Voice"}
              </Typography>
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => primaryMode === "agent" && setPicker("style")}
            disabled={primaryMode !== "agent"}
            sx={{
              ...pickerCardSx,
              opacity: primaryMode === "agent" ? 1 : 0.55,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              font: "inherit",
              color: "inherit",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1.5,
                flexShrink: 0,
                bgcolor: alpha("#000", 0.35),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${alpha("#fff", 0.12)}`,
              }}
            >
              <MovieFilterOutlined sx={{ color: "common.white", fontSize: 26 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} noWrap>
                {styleDisplayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lang?.videoAgentPickerStyle || "Style / Brand"}
              </Typography>
            </Box>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mt: 3.5, mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={800}>
            {lang?.videoAgentPromptLabel || "Prompt"}
          </Typography>
          <Typography variant="caption" color={script.length > 10000 ? "error" : "text.secondary"} fontWeight={600}>
            {script.length} / 10000
          </Typography>
        </Stack>
        <TextField
          fullWidth
          multiline
          minRows={isSm ? 7 : 9}
          maxRows={22}
          placeholder={lang?.videoAgentGlassPromptPh || "Describe your video idea…"}
          value={script}
          onChange={(e) => setScript(e.target.value)}
          inputProps={{ maxLength: 10000 }}
          InputProps={{
            sx: {
              alignItems: "flex-start",
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.07 : 0.035),
              fontSize: "1rem",
              lineHeight: 1.65,
            },
          }}
        />

        {primaryMode === "agent" && (
          <Card
            variant="outlined"
            sx={{
              mt: 3,
              borderRadius: 2.5,
              borderColor: alpha(theme.palette.primary.main, 0.22),
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.07 : 0.04),
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <PaletteOutlined fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={800}>
                  {lang?.videoAgentOutputSectionTitle || "Format & brand"}
                </Typography>
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 700 }}>
                {lang?.videoAgentOrientationLabel || "Orientation"}
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={agentOrientation}
                onChange={(_, v) => v != null && setAgentOrientation(v)}
                fullWidth
                sx={{
                  mb: 0.5,
                  gap: 0.5,
                  "& .MuiToggleButton-root": {
                    flex: 1,
                    py: 1.15,
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px !important",
                  },
                }}
              >
                <ToggleButton value="auto" disableRipple>
                  <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
                    <AspectRatioOutlined sx={{ fontSize: 20 }} />
                    <span>{lang?.videoAgentAuto || "Auto"}</span>
                  </Stack>
                </ToggleButton>
                <ToggleButton value="landscape" disableRipple>
                  <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
                    <CropLandscapeOutlined sx={{ fontSize: 20 }} />
                    <span>{lang?.videoAgentLandscape || "Landscape"}</span>
                  </Stack>
                </ToggleButton>
                <ToggleButton value="portrait" disableRipple>
                  <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
                    <CropPortraitOutlined sx={{ fontSize: 20 }} />
                    <span>{lang?.videoAgentPortrait || "Portrait"}</span>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>

              <Divider sx={{ my: 2.5 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={brandKitEnabled}
                    onChange={(_, c) => {
                      setBrandKitEnabled(c);
                      if (!c) setSelectedBrandKitId("");
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ ml: 0.5 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {lang?.videoAgentBrandKitSwitch || "Apply brand kit"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      {lang?.videoAgentBrandKitHint ||
                        "Uses your workspace colors, typography, and logo when HeyGen applies a kit."}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", mx: 0, mb: brandKitEnabled ? 1.5 : 0 }}
              />

              {brandKitEnabled ? (
                <FormControl fullWidth size="small" error={brandKits.length > 0 && !selectedBrandKitId}>
                  <InputLabel id="va-brand-kit-label">{lang?.videoAgentBrandKitSelect || "Brand kit"}</InputLabel>
                  <Select
                    labelId="va-brand-kit-label"
                    label={lang?.videoAgentBrandKitSelect || "Brand kit"}
                    value={selectedBrandKitId}
                    onChange={(e) => setSelectedBrandKitId(e.target.value)}
                    sx={{ borderRadius: 2 }}
                    disabled={brandKits.length === 0}
                  >
                    <MenuItem value="">
                      <em>{lang?.videoAgentBrandKitPlaceholder || "Choose a kit…"}</em>
                    </MenuItem>
                    {brandKits.map((kit) => (
                      <MenuItem key={kit.brand_kit_id} value={kit.brand_kit_id}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {kit.logo_url ? (
                            <Box
                              component="img"
                              src={kit.logo_url}
                              alt=""
                              sx={{ width: 28, height: 28, objectFit: "contain", borderRadius: 0.5 }}
                            />
                          ) : null}
                          <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                            {kit.name}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                  {brandKits.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                      {lang?.videoAgentBrandKitEmpty ||
                        "No brand kits in this workspace. Create one in your studio, then refresh."}
                    </Typography>
                  ) : null}
                  {brandKits.length > 0 && !selectedBrandKitId ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.75, display: "block" }}>
                      {lang?.videoAgentBrandKitRequired || "Select a brand kit to continue."}
                    </Typography>
                  ) : null}
                </FormControl>
              ) : null}
            </CardContent>
          </Card>
        )}

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{
            mt: 3,
            pt: 2.75,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Tooltip title={lang?.videoAgentAddMenuTip || "More options"}>
              <IconButton
                onClick={(e) => setAddMenuEl(e.currentTarget)}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={addMenuEl} open={Boolean(addMenuEl)} onClose={() => setAddMenuEl(null)}>
              <MenuItem
                onClick={() => {
                  setAddMenuEl(null);
                  setPrimaryMode("photo");
                  setPhotoDialogOpen(true);
                }}
              >
                {lang?.videoAgentTabPhoto || "Photo avatar (classic)"}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAddMenuEl(null);
                  setPrimaryMode("agent");
                }}
              >
                {lang?.videoAgentTabAgent || "Video Agent"}
              </MenuItem>
            </Menu>

            <Tooltip title={lang?.videoAgentMicTooltip || "Dictate prompt"}>
              <IconButton
                onClick={toggleMic}
                color={speechListening ? "error" : "default"}
                sx={{ border: 1, borderColor: "divider", bgcolor: alpha(theme.palette.text.primary, 0.04) }}
              >
                <MicOutlined fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={lang?.videoAgentPromptTip || "Prompt tips"}>
              <IconButton sx={{ border: 1, borderColor: "divider", bgcolor: alpha(theme.palette.text.primary, 0.04) }}>
                <LightbulbOutlined fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={lang?.videoAgentSettingsTip || "Photo mode (classic)"}>
              <IconButton
                onClick={() => setPhotoDialogOpen(true)}
                sx={{ border: 1, borderColor: "divider", bgcolor: alpha(theme.palette.text.primary, 0.04) }}
              >
                <TuneOutlined fontSize="small" />
              </IconButton>
            </Tooltip>

            {primaryMode === "photo" ? (
              <>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
                  <TimerOutlined sx={{ opacity: 0.7, fontSize: 20, display: { xs: "none", sm: "block" } }} />
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel>{lang?.videoAgentDurationLabel || "Duration"}</InputLabel>
                    <Select
                      label={lang?.videoAgentDurationLabel || "Duration"}
                      value={durationMode}
                      onChange={(e) => setDurationMode(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="auto">{lang?.videoAgentAuto || "Auto"}</MenuItem>
                      <MenuItem value="30">30s</MenuItem>
                      <MenuItem value="45">45s</MenuItem>
                      <MenuItem value="60">60s</MenuItem>
                      <MenuItem value="90">90s</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
                  <VideoCameraFrontOutlined sx={{ opacity: 0.7, fontSize: 20, display: { xs: "none", sm: "block" } }} />
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel>{lang?.videoAgentFormatLabel || "Aspect"}</InputLabel>
                    <Select
                      label={lang?.videoAgentFormatLabel || "Aspect"}
                      value={aspectMode}
                      onChange={(e) => setAspectMode(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="auto">{lang?.videoAgentAuto || "Auto"}</MenuItem>
                      <MenuItem value="16:9">16:9</MenuItem>
                      <MenuItem value="9:16">9:16</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <Tooltip title={lang?.videoAgentVisibilityTip || "Visibility (display only for now)"}>
                  <IconButton
                    onClick={() => setPrivateVis((p) => !p)}
                    sx={{ border: 1, borderColor: "divider", bgcolor: alpha(theme.palette.text.primary, 0.04) }}
                  >
                    {privateVis ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                  </IconButton>
                </Tooltip>

                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1 }}>
                  <AutoAwesomeOutlined sx={{ fontSize: 18, opacity: 0.75 }} />
                  <Typography variant="caption" fontWeight={700} sx={{ display: { xs: "none", sm: "block" } }}>
                    Seedance
                  </Typography>
                  <Switch size="small" checked={seedance} onChange={(e) => setSeedance(e.target.checked)} />
                </Stack>
              </>
            ) : null}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Button
              variant="contained"
              size="large"
              disabled={!canGenerate || generating}
              onClick={handleGenerate}
              startIcon={generating ? undefined : <SmartToyOutlined />}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                px: 3.5,
                py: 1.35,
                borderRadius: 2.5,
                minWidth: 148,
                boxShadow: `0 10px 32px ${alpha(theme.palette.primary.main, 0.42)}`,
              }}
            >
              {generating ? <CircularProgress size={22} color="inherit" /> : lang?.videoAgentGenerate || "Generate"}
            </Button>
          </Stack>
        </Stack>

        {(jobVideoId || jobSessionId || jobStatus) && (
          <Alert severity={jobVideoUrl ? "success" : "info"} sx={{ mt: 2.5, borderRadius: 2 }}>
            <Typography variant="body2" sx={{ mb: jobVideoUrl ? 1 : 0 }}>
              {(lang?.videoAgentJobStatus || "Status: {status}").replace("{status}", jobStatus || "…")}
              {jobVideoId ? ` · ${lang?.videoAgentJobVideoId || "Video"}: ${jobVideoId}` : ""}
              {jobSessionId ? ` · ${lang?.videoAgentJobSessionId || "Session"}: ${jobSessionId}` : ""}
            </Typography>
            {jobVideoUrl ? (
              <Link href={jobVideoUrl} target="_blank" rel="noopener noreferrer" fontWeight={700}>
                {lang?.videoAgentDownload || "Open / download video"}
              </Link>
            ) : null}
          </Alert>
        )}
      </Paper>
        </CommonDialog>
      </Box>
    </div>
  );
}
